import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import { Key, Lightning, FloppyDisk, Trash, PlugsConnected, ShieldCheck, Sparkle, ClockCounterClockwise } from "@phosphor-icons/react";

const PROVIDER_LABELS = { openai: "OpenAI", anthropic: "Anthropic", gemini: "Google Gemini" };

export default function Settings() {
  const [loaded, setLoaded] = useState(false);
  const [models, setModels] = useState({});
  const [useOwn, setUseOwn] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-5.4");
  const [hasKey, setHasKey] = useState(false);
  const [masked, setMasked] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [shareHistory, setShareHistory] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  const load = () => {
    api.get("/settings/ai").then((r) => {
      const d = r.data;
      setModels(d.available_models || {});
      setUseOwn(d.use_own_key); setProvider(d.provider); setModel(d.model);
      setHasKey(d.has_key); setMasked(d.key_masked); setShareHistory(!!d.share_history); setLoaded(true);
    }).catch(() => setLoaded(true));
  };
  useEffect(load, []);

  const toggleShareHistory = async () => {
    const next = !shareHistory;
    setShareHistory(next);
    setSavingPrivacy(true);
    try {
      await api.put("/settings/privacy", { share_history: next });
      toast.success(next ? "History sharing enabled — admins can now assist you" : "History sharing disabled");
    } catch (err) {
      setShareHistory(!next);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update setting");
    } finally { setSavingPrivacy(false); }
  };

  const providerModels = models[provider] || [];
  const onProvider = (p) => { setProvider(p); const m = (models[p] || [])[0]; if (m) setModel(m); };

  const save = async () => {
    setSaving(true);
    try {
      const body = { use_own_key: useOwn, provider, model };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const { data } = await api.put("/settings/ai", body);
      setHasKey(data.has_key); setMasked(data.key_masked); setUseOwn(data.use_own_key);
      setApiKey("");
      toast.success("AI settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const body = { provider, model };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const { data } = await api.post("/settings/ai/test", body);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test failed");
    } finally { setTesting(false); }
  };

  const removeKey = async () => {
    try {
      await api.delete("/settings/ai/key");
      setHasKey(false); setMasked(""); setUseOwn(false); setApiKey("");
      toast.success("Key removed — reverted to the built-in AI");
    } catch { toast.error("Could not remove key"); }
  };

  const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";
  const labelCls = "block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider";

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6">
        <h1 className="text-2xl tracking-tight flex items-center gap-2">
          <Key size={22} className="text-[#00F0FF]" /> Settings
        </h1>
        <p className="text-sm text-[#A1A1AA] font-mono mt-1">Bring your own AI — use your own provider key for generation.</p>
      </div>

      <div className="max-w-3xl p-8 space-y-6">
        <div className="border border-[#27272A] bg-[#0A0A0B] p-6 space-y-6" data-testid="ai-settings-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg tracking-tight flex items-center gap-2"><Sparkle size={18} className="text-[#00F0FF]" /> AI provider</h2>
              <p className="text-xs text-[#71717A] font-mono mt-1">When off, generation uses the built-in AI key.</p>
            </div>
            <button type="button" data-testid="ai-use-own-toggle" onClick={() => setUseOwn(!useOwn)}
              className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${useOwn ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5" : "border-[#27272A] text-[#71717A]"}`}>
              <span className={`w-8 h-4 rounded-full relative shrink-0 ${useOwn ? "bg-[#00FF41]/40" : "bg-[#27272A]"}`}>
                <span className={`absolute top-0.5 w-3 h-3 rounded-full transition-all duration-200 ${useOwn ? "left-4 bg-[#00FF41]" : "left-0.5 bg-[#71717A]"}`} />
              </span>
              {useOwn ? "Using my key" : "Built-in AI"}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Provider</label>
              <select data-testid="ai-provider-select" value={provider} onChange={(e) => onProvider(e.target.value)} className={inputCls}>
                {Object.keys(models).map((p) => (<option key={p} value={p}>{PROVIDER_LABELS[p] || p}</option>))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Model</label>
              <select data-testid="ai-model-select" value={model} onChange={(e) => setModel(e.target.value)} className={inputCls}>
                {providerModels.map((m) => (<option key={m} value={m}>{m}</option>))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>API key {hasKey && <span className="text-[#00FF41] normal-case">· saved {masked}</span>}</label>
            <input data-testid="ai-key-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              className={inputCls} placeholder={hasKey ? "•••••••• (leave blank to keep current)" : `Paste your ${PROVIDER_LABELS[provider]} API key`} />
            <p className="text-[10px] text-[#71717A] font-mono mt-2 flex items-center gap-1.5">
              <ShieldCheck size={13} className="text-[#00FF41]" /> Encrypted at rest · never shown again after saving · used only for your generations.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 pt-1">
            <button data-testid="ai-save-btn" onClick={save} disabled={saving}
              className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-5 py-2.5 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
              <FloppyDisk size={16} /> {saving ? "Saving…" : "Save settings"}
            </button>
            <button data-testid="ai-test-btn" onClick={test} disabled={testing || (!apiKey.trim() && !hasKey)}
              className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-5 py-2.5 text-sm hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-40">
              <PlugsConnected size={16} /> {testing ? "Testing…" : "Test connection"}
            </button>
            {hasKey && (
              <button data-testid="ai-remove-btn" onClick={removeKey}
                className="flex items-center gap-2 border border-[#27272A] text-[#A1A1AA] px-5 py-2.5 text-sm hover:border-[#FF5F56] hover:text-[#FF5F56] transition-colors duration-200">
                <Trash size={16} /> Remove key
              </button>
            )}
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#0A0A0B] p-6 space-y-4" data-testid="privacy-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg tracking-tight flex items-center gap-2">
                <ClockCounterClockwise size={18} className="text-[#00F0FF]" /> Share history for support
              </h2>
              <p className="text-xs text-[#71717A] font-mono mt-1 max-w-md">
                Let MDM Forge admins view your recent AI generations (prompts &amp; output) so they can troubleshoot
                or guide you. Off by default — nothing is stored until you turn this on.
              </p>
            </div>
            <button type="button" data-testid="share-history-toggle" onClick={toggleShareHistory} disabled={savingPrivacy}
              className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 disabled:opacity-50 ${shareHistory ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5" : "border-[#27272A] text-[#71717A]"}`}>
              <span className={`w-8 h-4 rounded-full relative shrink-0 ${shareHistory ? "bg-[#00FF41]/40" : "bg-[#27272A]"}`}>
                <span className={`absolute top-0.5 w-3 h-3 rounded-full transition-all duration-200 ${shareHistory ? "left-4 bg-[#00FF41]" : "left-0.5 bg-[#71717A]"}`} />
              </span>
              {shareHistory ? "Sharing on" : "Sharing off"}
            </button>
          </div>
          <p className="text-[10px] text-[#71717A] font-mono flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-[#00FF41]" /> Only the 100 most recent generations are retained · turn off anytime to stop logging.
          </p>
        </div>

        <div className="border border-[#27272A] bg-[#0f0f11] p-5 flex items-start gap-3">
          <Lightning size={18} weight="fill" className="text-[#00F0FF] mt-0.5 shrink-0" />
          <p className="text-[11px] text-[#A1A1AA] font-mono leading-relaxed">
            When "Using my key" is on, the AI Generator routes requests through your {PROVIDER_LABELS[provider]} account
            with the model above — billed to you, not MDM Forge. All the zero-token visual builders (Deploy Software,
            MenuBar Forge, Printers, SSO, Remote Desktop) never use any AI, so they're unaffected.
          </p>
        </div>
        {!loaded && <p className="text-xs text-[#71717A] font-mono">Loading…</p>}
      </div>
    </Layout>
  );
}
